import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import CustomButton from "components/common/Button/CustomButton";
import {
  PaEmptyState,
  PaPageHeader,
  PaSkeleton,
  PaStatusBadge,
  PaTabs,
} from "components/platform-admin";
import PlatformAdminLayout from "pages/PlatformAdmin/layout/PlatformAdminLayout";
import PaAccessDenied from "pages/PlatformAdmin/layout/PaAccessDenied";
import { USER_ROLE } from "Constants/constant";
import { platformRoleAccess } from "helpers/RolesPermission";
import { usePlatformAdmin } from "context/PlatformAdminContext";
import GhostStartModal from "pages/PlatformAdmin/Ghost/GhostStartModal";
import {
  activatePlatformTenant,
  cancelPlatformTenantSubscription,
  closePlatformTenantAccount,
  extendPlatformTenantTrial,
  fetchPlatformTenant,
  setPlatformTenantReadOnly,
  setPlatformTenantWildcard,
  suspendPlatformTenant,
} from "store/services/platformAdmin.services";
import AddTenantUserDrawer from "./AddTenantUserDrawer";
import TenantActionModal from "./TenantActionModal";
import TenantExtendTrialModal from "./TenantExtendTrialModal";
import TenantSupportDrawer from "./TenantSupportDrawer";
import TenantActivityTab from "./TenantActivityTab";
import TenantBillingTab from "./TenantBillingTab";
import TenantOverviewTab from "./TenantOverviewTab";
import TenantUsersTab from "./TenantUsersTab";
import TenantIntegrationsTab from "./TenantIntegrationsTab";
import TenantUsageTab from "../Usage/TenantUsageTab";
import TenantAiUsageTab from "../AiUsage/TenantAiUsageTab";
import { getTenantHeaderMetrics } from "./tenantOverview.utils";
import {
  buildTenantSupportPermissions,
  resolveTenantDetailCapabilities,
} from "./tenantDetailCapabilities.utils";
import {
  getReadOnlyAction,
  getWildcardAction,
} from "./tenantDetail.utils";
import { buildTenantDetailSupportReturnPath } from "../tenantDeepLink.utils";
import { getTenantStatusBadge } from "./tenantsList.utils";

const TenantDetail = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const canViewTenant = platformRoleAccess("platform.tenant.view");
  const capabilities = useMemo(() => resolveTenantDetailCapabilities(), []);
  const {
    canViewBilling,
    canManageBilling,
    canViewTenantUsers,
    canEnterTenant,
    canStartGhost,
    canSuspend,
    canReadOnly,
    canWildcard,
    canViewIntegrations,
    canViewUsage,
    canViewAiUsage,
    canViewAudit,
    canOpenSupportDrawer,
  } = capabilities;
  const { enterTenant, tenantContext, startGhost, ghostSubmitting } = usePlatformAdmin();

  const [ghostModalOpen, setGhostModalOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);

  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => searchParams.get("tab") || "overview");
  const [createdUsers, setCreatedUsers] = useState([]);
  const [actionModal, setActionModal] = useState(null);
  const [extendTrialOpen, setExtendTrialOpen] = useState(false);
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);

  const loadTenant = useCallback(async () => {
    if (!canViewTenant) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await fetchPlatformTenant(id);
      setTenant(data);
    } catch (err) {
      setTenant(null);
      setError(err?.message || "Failed to load tenant");
    } finally {
      setLoading(false);
    }
  }, [canViewTenant, id]);

  useEffect(() => {
    loadTenant();
  }, [loadTenant]);

  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    if (
      searchParams.get("support") === "1" &&
      tenant &&
      canOpenSupportDrawer &&
      !loading
    ) {
      setSupportOpen(true);
    }
  }, [searchParams, tenant, canOpenSupportDrawer, loading]);

  const handleTabChange = useCallback(
    (tabId) => {
      setActiveTab(tabId);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!tabId || tabId === "overview") {
            next.delete("tab");
          } else {
            next.set("tab", tabId);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const statusBadge = tenant ? getTenantStatusBadge(tenant.status) : null;
  const isSuspended = tenant?.status === "suspended";

  const supportPermissions = useMemo(
    () => buildTenantSupportPermissions(capabilities),
    [capabilities]
  );

  const handleSupportAction = (actionId, _target, playbook) => {
    if (!tenant) {
      return;
    }

    const tenantReturnPath = buildTenantDetailSupportReturnPath(tenant.id);

    if (actionId === "enter_tenant") {
      enterTenant({
        spId: tenant.id,
        spName: tenant.name,
        returnTo: tenantReturnPath,
      });
      setSupportOpen(false);
      return;
    }

    if (actionId === "enter_tenant_path") {
      enterTenant({
        spId: tenant.id,
        spName: tenant.name,
        path: playbook?.primaryAction?.path || playbook?.tenantPath,
        returnTo: tenantReturnPath,
      });
      setSupportOpen(false);
      return;
    }

    if (actionId === "ghost") {
      setSupportOpen(false);
      setGhostModalOpen(true);
      return;
    }

    if (actionId === "open_billing") {
      handleTabChange("billing");
      setSupportOpen(false);
      return;
    }

    if (actionId === "extend_trial") {
      setSupportOpen(false);
      setExtendTrialOpen(true);
      return;
    }

    const modalConfig = {
      suspend: {
        type: "suspend",
        title: "Suspend tenant",
        description: "Tenant users will be blocked from signing in.",
        confirmLabel: "Suspend tenant",
        destructive: true,
      },
      activate: {
        type: "activate",
        title: "Activate tenant",
        description: "Restore tenant access for users.",
        confirmLabel: "Activate tenant",
      },
      cancel_subscription_immediate: {
        type: "cancel_subscription_immediate",
        title: "Cancel subscription now",
        description:
          "Stops Stripe billing immediately and enables read-only mode for this tenant.",
        confirmLabel: "Cancel subscription",
        destructive: true,
      },
      cancel_subscription_period_end: {
        type: "cancel_subscription_period_end",
        title: "Cancel at period end",
        description: "No renewal after the current billing period. Access continues until then.",
        confirmLabel: "Schedule cancellation",
        destructive: true,
      },
      close_account: {
        type: "close_account",
        title: "Close account",
        description:
          "Cancels Stripe (if active), enables read-only, and suspends the tenant.",
        confirmLabel: "Close account",
        destructive: true,
      },
      read_only_on: {
        type: "read_only",
        enabled: true,
        title: "Enable read-only",
        description: "Block writes while keeping logins active.",
        confirmLabel: "Enable read-only",
      },
      read_only_off: {
        type: "read_only",
        enabled: false,
        title: "Lift read-only",
        description: "Allow writes again for this tenant.",
        confirmLabel: "Lift read-only",
      },
    }[actionId];

    if (modalConfig) {
      setSupportOpen(false);
      setActionModal(modalConfig);
    }
  };

  const headerActions = useMemo(() => {
    if (!tenant) {
      return null;
    }

    return (
      <div className="flex flex-wrap items-center gap-2">
        {canOpenSupportDrawer ? (
          <button
            type="button"
            onClick={() => setSupportOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100"
          >
            Support
          </button>
        ) : null}
        {canEnterTenant ? (
          <button
            type="button"
            disabled={tenantContext?.spId === tenant.id}
            title={
              tenantContext?.spId === tenant.id
                ? "Already operating in this tenant"
                : "Enter tenant context"
            }
            onClick={() =>
              enterTenant({
                spId: tenant.id,
                spName: tenant.name,
                returnTo: buildTenantDetailSupportReturnPath(tenant.id),
              })
            }
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
          >
            Enter tenant
          </button>
        ) : null}
        {canStartGhost ? (
          <button
            type="button"
            disabled={!tenant.owner?.id}
            title={!tenant.owner?.id ? "Tenant owner not available" : "Ghost as this user"}
            onClick={() => setGhostModalOpen(true)}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
          >
            Ghost as user
          </button>
        ) : null}
        {canSuspend && !isSuspended ? (
          <CustomButton
            label="Suspend"
            variant="danger"
            clickHandler={() =>
              setActionModal({
                type: "suspend",
                title: "Suspend tenant",
                description: "Tenant users will be blocked from signing in.",
                confirmLabel: "Suspend tenant",
                destructive: true,
              })
            }
            cssClass="bg-red-600 hover:bg-red-700 text-white"
          />
        ) : null}
        {canSuspend && isSuspended ? (
          <CustomButton
            label="Activate"
            clickHandler={() =>
              setActionModal({
                type: "activate",
                title: "Activate tenant",
                description: "Restore tenant access for users.",
                confirmLabel: "Activate tenant",
              })
            }
            cssClass="bg-indigo-600 hover:bg-indigo-700 text-white"
          />
        ) : null}
        {canReadOnly ? (
          <CustomButton
            label={getReadOnlyAction(tenant).title}
            clickHandler={() => {
              const action = getReadOnlyAction(tenant);
              setActionModal({
                type: "read_only",
                enabled: action.enabled,
                title: action.title,
                description: action.description,
                confirmLabel: action.enabled ? "Enable read-only" : "Lift read-only",
              });
            }}
            cssClass="border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          />
        ) : null}
        {canWildcard ? (
          <CustomButton
            label={getWildcardAction(tenant).title}
            clickHandler={() => {
              const action = getWildcardAction(tenant);
              setActionModal({
                type: "wildcard",
                enabled: action.enabled,
                title: action.title,
                description: action.description,
                confirmLabel: action.enabled ? "Enable wildcard" : "Remove wildcard",
              });
            }}
            cssClass="border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          />
        ) : null}
      </div>
    );
  }, [tenant, canOpenSupportDrawer, canEnterTenant, canStartGhost, canSuspend, canReadOnly, canWildcard, isSuspended, enterTenant, tenantContext]);

  const handleActionSubmit = async (reason) => {
    if (!actionModal || !tenant) {
      return;
    }

    setActionSubmitting(true);

    try {
      let updatedTenant = null;

      switch (actionModal.type) {
        case "suspend":
          updatedTenant = await suspendPlatformTenant(tenant.id, reason);
          toast.success("Tenant suspended");
          break;
        case "activate":
          updatedTenant = await activatePlatformTenant(tenant.id, reason);
          toast.success("Tenant activated");
          break;
        case "read_only":
          updatedTenant = await setPlatformTenantReadOnly(
            tenant.id,
            actionModal.enabled,
            reason
          );
          toast.success(
            actionModal.enabled ? "Read-only mode enabled" : "Read-only mode lifted"
          );
          break;
        case "wildcard":
          updatedTenant = await setPlatformTenantWildcard(
            tenant.id,
            actionModal.enabled,
            reason
          );
          toast.success(
            actionModal.enabled ? "Wildcard enabled" : "Wildcard removed"
          );
          break;
        case "cancel_subscription_immediate":
          updatedTenant = await cancelPlatformTenantSubscription(
            tenant.id,
            reason,
            "immediate"
          );
          toast.success("Subscription cancelled");
          break;
        case "cancel_subscription_period_end":
          updatedTenant = await cancelPlatformTenantSubscription(
            tenant.id,
            reason,
            "period_end"
          );
          toast.success("Subscription scheduled to cancel at period end");
          break;
        case "close_account":
          updatedTenant = await closePlatformTenantAccount(tenant.id, reason);
          toast.success("Account closed");
          break;
        default:
          break;
      }

      if (updatedTenant) {
        setTenant(updatedTenant);
      } else {
        await loadTenant();
      }

      setActionModal(null);
    } catch (err) {
      toast.error(err?.message || "Action failed");
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleNavigateTab = useCallback(
    (tabId) => {
      if (tabId) {
        handleTabChange(tabId);
      }
    },
    [handleTabChange]
  );

  const headerMetrics = useMemo(
    () => (tenant ? getTenantHeaderMetrics(tenant, { canViewBilling }) : []),
    [tenant, canViewBilling]
  );

  const tabs = useMemo(
    () => [
      {
        id: "overview",
        label: "Overview",
        content: (
          <TenantOverviewTab
            tenant={tenant}
            tenantId={tenant?.id}
            onNavigateTab={handleNavigateTab}
            capabilities={capabilities}
          />
        ),
      },
      ...(canViewTenantUsers
        ? [
            {
              id: "users",
              label: "Users",
              content: (
                <TenantUsersTab
                  tenant={tenant}
                  createdUsers={createdUsers}
                  onAddUser={() => setAddUserOpen(true)}
                />
              ),
            },
          ]
        : []),
      ...(canViewIntegrations
        ? [
            {
              id: "integrations",
              label: "Integrations",
              content: <TenantIntegrationsTab tenantId={tenant?.id} />,
            },
          ]
        : []),
      ...(canViewUsage
        ? [
            {
              id: "usage",
              label: "Usage",
              content: <TenantUsageTab tenantId={tenant?.id} />,
            },
          ]
        : []),
      ...(canViewAiUsage
        ? [
            {
              id: "ai-usage",
              label: "AI Usage",
              content: <TenantAiUsageTab tenantId={tenant?.id} />,
            },
          ]
        : []),
      ...(canViewBilling
        ? [
            {
              id: "billing",
              label: "Billing",
              content: <TenantBillingTab tenant={tenant} onExtendTrial={() => setExtendTrialOpen(true)} />,
            },
          ]
        : []),
      ...(canViewAudit
        ? [
            {
              id: "activity",
              label: "Activity",
              content: (
                <TenantActivityTab tenantId={tenant?.id} tenantName={tenant?.name} />
              ),
            },
          ]
        : []),
    ],
    [
      tenant,
      createdUsers,
      capabilities,
      canViewTenantUsers,
      canViewIntegrations,
      canViewUsage,
      canViewAiUsage,
      canViewBilling,
      canViewAudit,
      handleNavigateTab,
    ]
  );

  const allowedTabIds = useMemo(() => tabs.map((tab) => tab.id), [tabs]);

  useEffect(() => {
    if (!loading && tenant && activeTab && !allowedTabIds.includes(activeTab)) {
      handleTabChange("overview");
    }
  }, [loading, tenant, activeTab, allowedTabIds, handleTabChange]);

  if (!canViewTenant) {
    return (
      <PaAccessDenied
        title="Tenant detail"
        description="You need platform.tenant.view to open tenant detail."
      />
    );
  }

  const activeTabLabel = tabs.find((tab) => tab.id === activeTab)?.label;

  return (
    <PlatformAdminLayout>
      <nav className="mb-4 text-sm text-gray-500" aria-label="Breadcrumb">
        <Link to="/tenants" className="text-indigo-600 hover:text-indigo-700">
          Tenants
        </Link>
        <span className="mx-2">/</span>
        <Link
          to={`/tenants/${id}`}
          className="text-indigo-600 hover:text-indigo-700"
        >
          {tenant?.name || "Tenant"}
        </Link>
        {activeTab !== "overview" && activeTabLabel ? (
          <>
            <span className="mx-2">/</span>
            <span className="text-gray-700">{activeTabLabel}</span>
          </>
        ) : null}
      </nav>

      {loading ? (
        <div className="space-y-4">
          <PaSkeleton variant="text" lines={2} />
          <PaSkeleton variant="card" />
          <PaSkeleton variant="table" lines={4} />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : (
        <>
          <PaPageHeader
            title={tenant?.name}
            subtitle={tenant?.email}
            actions={
              <div className="flex flex-col items-end gap-3">
                {statusBadge ? (
                  <PaStatusBadge label={statusBadge.label} variant={statusBadge.variant} />
                ) : null}
                {headerActions}
              </div>
            }
          />

          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {headerMetrics.map((metric) => (
              <div
                key={metric.id}
                className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm"
              >
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {metric.label}
                </p>
                <p className="mt-1 text-lg font-semibold text-gray-900">{metric.value}</p>
              </div>
            ))}
          </div>

          <PaTabs tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />
        </>
      )}

      <TenantActionModal
        open={Boolean(actionModal)}
        onClose={() => setActionModal(null)}
        title={actionModal?.title}
        description={actionModal?.description}
        confirmLabel={actionModal?.confirmLabel}
        destructive={actionModal?.destructive}
        submitting={actionSubmitting}
        onSubmit={handleActionSubmit}
      />

      <TenantExtendTrialModal
        open={extendTrialOpen}
        onClose={() => setExtendTrialOpen(false)}
        currentTrialEndDate={tenant?.subscription?.trial_end_date}
        submitting={actionSubmitting}
        onSubmit={async ({ reason, trialEndDate }) => {
          if (!tenant?.id) {
            return;
          }

          setActionSubmitting(true);

          try {
            const updatedTenant = await extendPlatformTenantTrial(
              tenant.id,
              reason,
              trialEndDate
            );
            toast.success("Trial extended");
            setExtendTrialOpen(false);
            if (updatedTenant) {
              setTenant(updatedTenant);
            } else {
              await loadTenant();
            }
          } catch (err) {
            toast.error(err?.message || "Failed to extend trial");
          } finally {
            setActionSubmitting(false);
          }
        }}
      />

      <TenantSupportDrawer
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        tenant={tenant}
        permissions={supportPermissions}
        onRunAction={handleSupportAction}
      />

      <AddTenantUserDrawer
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        tenantId={tenant?.id}
        onCreated={(user) => {
          if (user) {
            setCreatedUsers((prev) => [user, ...prev]);
            handleTabChange("users");
          }
        }}
      />

      <GhostStartModal
        open={ghostModalOpen}
        onClose={() => setGhostModalOpen(false)}
        title="Ghost as user"
        description="You will operate the portal as this user. Your access is limited to what they can do. All actions are audited."
        targetLabel={
          tenant?.owner?.name
            ? `${tenant.owner.name} (${tenant.owner.email})`
            : tenant?.owner?.email
        }
        submitting={ghostSubmitting}
        allowReadOnly
        onSubmit={async ({ reason, readOnly }) => {
          if (!tenant?.owner?.id) {
            return;
          }

          try {
            await startGhost({
              userId: tenant.owner.id,
              reason,
              readOnly,
              spId: tenant.id,
              actingUser: {
                id: tenant.owner.id,
                name: tenant.owner.name,
                email: tenant.owner.email,
                user_type: tenant.owner.user_type || USER_ROLE.Sp_Admin,
              },
              tenantName: tenant.name,
              tenantFlags: {
                suspended: tenant.status === "suspended",
                read_only: Boolean(tenant.flags?.read_only),
              },
            });
            setGhostModalOpen(false);
          } catch (err) {
            toast.error(err?.message || "Failed to start ghost session");
          }
        }}
      />
    </PlatformAdminLayout>
  );
};

export default TenantDetail;
